import { PureObject } from "@pure-ptr/core";
import { http, restEndpoint } from "./common";

export class ContractInfo extends PureObject {
    id : number;
    docsCount = 0;
    processedDocsCount = 0
    failedDocsCount = 0;
    chatSessionsCount = 0;
    lastChatSessionId = 0;

    documents : ContractDocumentInfo[] = [];

    async fetchDocuments() {
        const json = await http.get( '/ai-api/v3/contract-info/' + this.id + '/documents' );
        return ContractDocumentInfo.array( json );
    }

    async uploadDocumenToBot(contractId,options) {
        const json = await http.post( `/ai-api/v3/contract-info/${contractId}/documents`,options );
        return json
    }

    async uploadDocumentStatus(docId) {
        const json = await http.get(`/ai-api/v1/contract-info/${docId}/documents`);
        return json
    }

    async retryFailedDocuments(contractId, documentData) {
        // Parse metadata if it's a string
        let metadata = {};
        if (documentData.metadata) {
            try {
                metadata = typeof documentData.metadata === 'string' 
                    ? JSON.parse(documentData.metadata) 
                    : documentData.metadata;
            } catch (e) {
                console.warn('Failed to parse metadata:', e);
            }
        }
        
        const payload = {
            s3_key: documentData.s3Key || documentData.s3_key,
            filename: documentData.documentName || documentData.filename,
            contract_id: contractId,
            document_id: documentData.id,
            metadata: metadata,
            description: documentData.description || ''
        };
        const json = await http.post(`/ai-api/v3/contract-info/${contractId}/documents?retry_flag=true`, payload);
        return json
    }

    async createNewChatSession(options) {
        const json = await http.post( `/ai-api/v2/chat-session`,options );
        return json
    }
     async addUserMessageToSession(sessionId,options) {
        const json = await http.post( `/ai-api/v1/chat-session/${sessionId}/messages/`,options );
        return json
    }

    async getAllSessionMessages(sessionId: string, lastMessageId?: string)  {
        let params = "";
        if (lastMessageId) {
            params += `&lastMessageId=${lastMessageId}`;
        }
        params += `&limit=200&page=0`;
        const json = await http.get( `/ai-api/v2/chat-session/${sessionId}/messages?${params}`,null,'x-ndjson');
        return json
    }
    async getAllChatSessions(id, type) {
        let queryParam = '';

        if (type === 'claim') {
            queryParam = `type=${type}&claim_analysis_details_id=${id}`;
        } else {
            queryParam = `type=${type}&contractId=${id}`;
        }

        const json = await http.get(`/ai-api/v2/chat-session?${queryParam}&limit=200&page=0`);
        // in json add new field 'serial' to each object
        json.forEach((item, index) => {
            item.serial = index + 1;
        });
        return json;
    }

    async getAICreditLimits(clientId) {
        const json = await http.get( `/api/v1/credit-accounts/usage/${clientId}`);
        return json
    }
    async deleteChatSession(session_id) {
        const json = await http.del( `/ai-api/v1/chat-session/${session_id}/messages`);
        return json
    }
     async uploadAllDocumentsToBot(contractId,options) {
        const json = await http.post( `/ai-api/v2/contract-info/${contractId}/all_documents`,options );
        return json
    }
    async addUserMessageToSessionStreamed(sessionId, options, isRetry = false) {
        const retryParam = isRetry ? '?is_retry=true' : '';
        const json = await http.post( `/ai-api/v3/chat-session/${sessionId}/messages/stream${retryParam}`, options, 'json',
        undefined,
        true );
        return json
    }
    async getSessionDetails(sessionId) {
        const json = await http.get( `/ai-api/v1/chat-session/${sessionId}`);
        return json
    }

    // update title of current chat
    async updateChatSessionTitle(sessionId, title) {
        const json = await http.patch( `/ai-api/v1/chat-session/${sessionId}/title`, { title });
        return json;
    }


    // Generate summary of claims
    async getClaimsSummary(claimId, payload={}) {
        const json = await http.post(`/ai-api/v1/claim-summary/${claimId}`,payload);
        return json;
    }

    // Streamed claim summary - returns raw fetch Response so caller can read response.body.getReader()
    async getClaimsSummaryStream(claimId, payload = {}) {
        const res = await http.post(
            `/ai-api/v1/claim-summary/stream/${claimId}`,
            payload,
            'json',
            undefined,
            true
        );
        return res;
    }

    async poolSummaryData(claimNumber) {
        const json = await http.get(`/ai-api/v1/claim-summary/status/${claimNumber}`);
        return json
    }

    // Generate summary of claims

    async poolSummaryDataContract(contractId) {
        const json = await http.get(`/ai-api/v1/contract-summary/status/${contractId}`);
        return json
    }

    async deleteDocuments(documentId) {
        const json = await http.del(`/api/v2/cms/document/${documentId}`);
        return json
    }

    async refreshContractAfterDocumentDeletion(contractId) {
        const json = await http.post(`/ai-api/v2/contract-info/${contractId}/refresh-after-deletion`,null);
        return json
    }

    // GuardRails integration
    async getGuardRailsAnalysis(message, ignoreGuardrails = false) {
        const json = await http.post(`/ai-api/chat/check_message_guardrail`, { 
            "body": message,
            "is_filter_search": ignoreGuardrails
        });
        return json;
    }

    // AI Claim Search - natural language query
    async searchClaims(query: string) {
        const json = await http.post(`/ai-api/v1/claim-filters`, {
            query
        });
        return json;
    }
    // Stop streaming response
    async stopResponse(sessionId: number) {
        try {
            const json = await http.post(`/ai-api/v1/chat-session/${sessionId}/messages/stop`, {});
            return json;
        } catch (error) {
            console.error('Stop response API error:', error);
            return { status: 'error' };
        }
    }
}

export const contractInfoEndpoint = restEndpoint( ContractInfo, '/ai-api/v1/contract-info' );

export class ContractDocumentInfo extends PureObject {
    id : number;
    documentUrl = '';
    documentName = '';
    metadata = { "type": "" };
    description = '';
}

